import {PageHeader} from '../ui/PageHeader';
import {useEffect,useRef,useState} from 'react';
import {useSearchParams} from 'react-router';
import {Link} from '../ui/ScopedLink';
import {api} from '../api';
import {formatDateTime} from '../dates';
import {MailNavigation} from '../ui/MailNavigation';
import {Empty,ErrorState,Loading} from '../ui/State';
import {useScopePending,useStaffSession} from '../ui/StaffSession';
import {withBuildingScope} from '../ui/building-scope';

type Queue='active'|'overdue'|'complaints'|'resolved';
type Population='current'|'history'|'all';
type CaseRecord={
 id:string;case_number:string;thread_id:string;thread_number:string;title:string;status:string;
 priority:string;case_type:string;complaint_stage:string;development_name?:string;assigned_name?:string;
 response_due_at?:string;resolution_due_at?:string;response_overdue:boolean;resolution_overdue:boolean;
 first_response_evidenced?:boolean;sla_policy_name?:string;historical?:boolean;
};
type CasePage={items:CaseRecord[];total:number;page:number;pageSize:number;totalPages:number};
type CasePageResponse=Pick<CasePage,'items'|'total'>&Partial<Omit<CasePage,'items'|'total'>>&{building?:string|null;population?:Population};
const queues:Array<[Queue,string]>=[['active','Open'],['overdue','Overdue'],['complaints','Complaints'],['resolved','Resolved']];
const due=(value?:string)=>formatDateTime(value,'Not set');
const pageSize=50;
const populationExplanations:Record<Population,string>={
 current:'Current correspondence and explicitly owned cases. Historical records remain available in Retained history.',
 history:'Historical correspondence is retained for review. Its recorded dates do not create a new response commitment. These cases have not been automatically resolved.',
 all:'All retained cases, including historical correspondence. Recorded dates and unresolved status are preserved.',
};
function checkedPage(result:CasePageResponse,page:number):CasePage{
 if(!Array.isArray(result.items)||!Number.isSafeInteger(result.total)||result.total<0)
  throw new Error('The case list could not be checked. Please try again.');
 const totalPages=Math.max(1,Math.ceil(result.total/pageSize));
 // An earlier API may still serve an in-flight request during deployment. Keep every record reachable.
 if(result.page===undefined&&result.pageSize===undefined&&result.totalPages===undefined){
  if(result.items.length!==result.total)throw new Error('The complete case list is unavailable. Please try again.');
  return {items:result.items.slice((page-1)*pageSize,page*pageSize),total:result.total,page,pageSize,totalPages};
 }
 if(result.page!==page||result.pageSize!==pageSize||result.totalPages!==totalPages
  ||result.items.length!==Math.min(pageSize,Math.max(0,result.total-(page-1)*pageSize)))
  throw new Error('The case page could not be checked. Please try again.');
 return {...result,page,pageSize,totalPages};
}

export function CommunicationCases(){
 const session=useStaffSession()!,scopePending=useScopePending(),building=session.effectiveScope.kind==='building'?session.effectiveScope.id:undefined;
 const[params,setParams]=useSearchParams();
 const queue=queues.find(([value])=>value===params.get('queue'))?.[0]??'active';
 const population:Population=params.get('population')==='history'?'history':params.get('population')==='all'?'all':'current';
 const rawPage=Number(params.get('page')??1),page=Number.isSafeInteger(rawPage)&&rawPage>=1&&rawPage<=1000000?rawPage:1;
 const[loaded,setData]=useState<CasePage&{scopeKey:string}>(),[error,setError]=useState<unknown>(),[attempt,setAttempt]=useState(0);
 const scopeKey=[building??'portfolio',population,queue,page].join(':'),data=loaded?.scopeKey===scopeKey?loaded:undefined;
 const heading=useRef<HTMLHeadingElement>(null),moveFocus=useRef(false);
 const select=(nextQueue:Queue,nextPage:number,nextPopulation=population)=>{
  moveFocus.current=true;
  setParams(current=>{
   const next=new URLSearchParams(current);
   nextQueue==='active'?next.delete('queue'):next.set('queue',nextQueue);
   nextPage===1?next.delete('page'):next.set('page',String(nextPage));
   nextPopulation==='current'?next.delete('population'):next.set('population',nextPopulation);
   return next;
  });
 };
 useEffect(()=>{
  let current=true;setData(undefined);setError(undefined);
  api<CasePageResponse>(withBuildingScope(`/api/communications/cases?queue=${queue}&population=${population}&page=${page}&limit=${pageSize}`,building))
   .then(result=>{if(current){
    if((building&&result.building!==building)||(result.population!==population&&!(population==='all'&&result.population===undefined)))
     throw new Error('The cases could not be confirmed for this view. Please try again.');
    setData({...checkedPage(result,page),scopeKey});
   }})
   .catch(problem=>{if(current)setError(problem)});
  return()=>{current=false};
 },[queue,page,attempt,building,population]);
 useEffect(()=>{if(data&&moveFocus.current){moveFocus.current=false;heading.current?.focus();}},[data]);
 if(scopePending)return <Loading/>;
 return <>
  <div className="mail-utility-row"><MailNavigation/></div>
  <PageHeader headingRef={heading} title="Cases & deadlines"/>
  <label htmlFor="case-population">Case records</label>
  <select id="case-population" value={population} onChange={event=>select(queue,1,event.currentTarget.value as Population)}>
   <option value="current">Current work</option><option value="history">Historical cases only</option><option value="all">Current and historical cases</option>
  </select>
  <details className="case-view-help"><summary>About these views</summary><p>{populationExplanations[population]}</p></details>
  <nav className="case-queue-tabs" aria-label="Case queues">{queues.map(([value,label])=>
   <button key={value} className={queue===value?'active':''} aria-pressed={queue===value} onClick={()=>select(value,1)}>{label}</button>)}</nav>
  {error?<ErrorState error={error} subject="Cases" onRetry={()=>{moveFocus.current=true;setAttempt(value=>value+1)}}/>
   :!data?<Loading/>:!data.items.length?<Empty>{page>1&&data.total>0
    ?'No cases on this page. Return to earlier results.':'No cases require attention in this view.'}</Empty>
    :<section className="communication-case-list" aria-label={`${queue} communication cases`}>{data.items.map(item=>
     <article className={`communication-case-row ${(item.response_overdue||item.resolution_overdue)?'overdue':''}`} key={item.id}>
      <div><span className="eyebrow">{item.case_number} · {item.case_type}</span>
       <h2><Link to={`/communications/threads/${item.thread_id}`}>{item.title}</Link></h2>
       <p>{item.development_name??'Portfolio-wide'} · {item.assigned_name??'Unassigned'} · {item.priority}</p>
       {item.historical&&<p>Historical correspondence</p>}
      </div>
      <dl><div><dt>First response</dt><dd className={item.response_overdue?'late':''}>{item.first_response_evidenced
       ?'Reply recorded':['Resolved','Closed'].includes(item.status)?'Not recorded':due(item.response_due_at)}</dd></div>
       <div><dt>Resolution</dt><dd className={item.resolution_overdue?'late':''}>{due(item.resolution_due_at)}</dd></div>
       <div><dt>Status</dt><dd>{item.status}</dd></div>
      </dl>
     </article>)}</section>}
  {data&&!error&&(data.totalPages>1||page>1)&&<nav className="communication-pagination" aria-label="Case pages">
   <span role="status">{data.items.length?`Showing ${(page-1)*pageSize+1}–${(page-1)*pageSize+data.items.length} of ${data.total}`
    :`0 cases on this page · ${data.total} matching`}</span>
   <div><button disabled={page<=1} onClick={()=>select(queue,page-1)}>Previous</button>
    <span>{page>data.totalPages?`Page ${page} · ${data.totalPages} ${data.totalPages===1?'page':'pages'} of current results`
     :`Page ${page} of ${data.totalPages}`}</span>
    <button disabled={page>=data.totalPages} onClick={()=>select(queue,page+1)}>Next</button>
    {page>data.totalPages&&<button onClick={()=>select(queue,1)}>First page</button>}
   </div>
  </nav>}
 </>;
}
